import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { audienceModeTestHelpers } from '@/utils/audienceModeTestHelpers';
import { 
  CheckCircle2, 
  XCircle, 
  Activity, 
  Database, 
  Users, 
  Tv,
  AlertCircle,
  Play,
  FileText
} from 'lucide-react';

export default function AudienceModeTest() {
  const { user } = useUnifiedUser();
  const [testResults, setTestResults] = useState<{
    database?: { success: boolean; issues: string[] };
    mediaOutlets?: { success: boolean; message: string };
    sessions?: { success: boolean; message: string };
    customOutlets?: { success: boolean; message: string };
  }>({});
  const [systemHealth, setSystemHealth] = useState<{
    healthy: boolean;
    stats: {
      totalOutlets: number;
      activeSessions: number;
      pendingReviews: number;
    };
  } | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    loadSystemHealth();
  }, []);

  const loadSystemHealth = async () => {
    const health = await audienceModeTestHelpers.getSystemHealth();
    setSystemHealth(health);
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults({});

    // Test 1: Database
    const dbResult = await audienceModeTestHelpers.validateDatabaseSetup();
    setTestResults(prev => ({ ...prev, database: dbResult }));

    // Test 2: Media Outlets
    const outletResult = await audienceModeTestHelpers.testMediaOutletOperations();
    setTestResults(prev => ({ ...prev, mediaOutlets: outletResult }));

    // Test 3 & 4: Sessions and Custom Outlets (if user logged in)
    if (user) {
      const testGameId = crypto.randomUUID(); // Mock game ID for testing
      
      const sessionResult = await audienceModeTestHelpers.testSessionCreation(
        user.id,
        user.userType,
        testGameId
      );
      setTestResults(prev => ({ ...prev, sessions: sessionResult }));

      const customResult = await audienceModeTestHelpers.testCustomOutletSubmission(
        user.id,
        user.userType
      );
      setTestResults(prev => ({ ...prev, customOutlets: customResult }));
    }

    await loadSystemHealth();
    setIsRunning(false);
  };

  const renderTestResult = (
    name: string,
    result?: { success: boolean; message?: string; issues?: string[] }
  ) => {
    if (!result) {
      return (
        <div className="flex items-center gap-3 p-4 rounded-lg border">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <div className="font-medium">{name}</div>
            <div className="text-sm text-muted-foreground">Not run yet</div>
          </div>
        </div>
      );
    }

    return (
      <div className={`flex items-center gap-3 p-4 rounded-lg border ${
        result.success ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'
      }`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          result.success ? 'bg-green-500/10' : 'bg-red-500/10'
        }`}>
          {result.success ? (
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
        </div>
        <div className="flex-1">
          <div className="font-medium">{name}</div>
          <div className="text-sm text-muted-foreground">
            {result.message || (result.issues && result.issues.length > 0 
              ? result.issues.join(', ') 
              : 'All checks passed')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Activity className="w-8 h-8" />
              Audience Mode Testing
            </h1>
            <p className="text-muted-foreground mt-1">
              Validate system functionality and check health status
            </p>
          </div>
          <Button onClick={runAllTests} disabled={isRunning} size="lg">
            {isRunning ? (
              <>
                <Activity className="w-4 h-4 mr-2 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run All Tests
              </>
            )}
          </Button>
        </div>

        {/* User Status */}
        {!user && (
          <Alert>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Some tests require authentication. Please log in to run full validation.
            </AlertDescription>
          </Alert>
        )}

        {/* System Health */}
        {systemHealth && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                System Health
              </CardTitle>
              <CardDescription>Current system statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 rounded-lg bg-accent/50">
                  <div className="flex items-center justify-between">
                    <Tv className="w-8 h-8 text-primary" />
                    <Badge variant="secondary" className="text-lg">
                      {systemHealth.stats.totalOutlets}
                    </Badge>
                  </div>
                  <div className="mt-2 font-medium">Media Outlets</div>
                  <div className="text-sm text-muted-foreground">Total available</div>
                </div>

                <div className="p-4 rounded-lg bg-accent/50">
                  <div className="flex items-center justify-between">
                    <Users className="w-8 h-8 text-primary" />
                    <Badge variant="secondary" className="text-lg">
                      {systemHealth.stats.activeSessions}
                    </Badge>
                  </div>
                  <div className="mt-2 font-medium">Active Sessions</div>
                  <div className="text-sm text-muted-foreground">Currently watching</div>
                </div>

                <div className="p-4 rounded-lg bg-accent/50">
                  <div className="flex items-center justify-between">
                    <FileText className="w-8 h-8 text-primary" />
                    <Badge variant="secondary" className="text-lg">
                      {systemHealth.stats.pendingReviews}
                    </Badge>
                  </div>
                  <div className="mt-2 font-medium">Pending Reviews</div>
                  <div className="text-sm text-muted-foreground">Awaiting approval</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Test Results */}
        <Card>
          <CardHeader>
            <CardTitle>Validation Tests</CardTitle>
            <CardDescription>
              Automated tests to verify all features are working correctly
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {renderTestResult('Database Schema & RLS', testResults.database)}
            {renderTestResult('Media Outlet Operations', testResults.mediaOutlets)}
            {renderTestResult('Session Creation', testResults.sessions)}
            {renderTestResult('Custom Outlet Submission', testResults.customOutlets)}
          </CardContent>
        </Card>

        {/* Testing Checklist */}
        <Card>
          <CardHeader>
            <CardTitle>Manual Testing Checklist</CardTitle>
            <CardDescription>Features to verify manually</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Admin: Manage Media Outlets</div>
                  <div className="text-sm text-muted-foreground">
                    Create, edit, and delete TV/Radio outlets
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Admin: Review Custom Submissions</div>
                  <div className="text-sm text-muted-foreground">
                    Approve/reject custom outlet requests
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">User: Start Audience Session</div>
                  <div className="text-sm text-muted-foreground">
                    Join as onsite, watching TV, or listening to radio
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">User: Select Media Outlet</div>
                  <div className="text-sm text-muted-foreground">
                    Choose from existing outlets or submit custom
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Real-time Stats Updates</div>
                  <div className="text-sm text-muted-foreground">
                    Verify audience count updates in real-time
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Toast Notifications</div>
                  <div className="text-sm text-muted-foreground">
                    Check feedback messages for all actions
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Console Access */}
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            <strong>Developer Tip:</strong> Access test helpers in the browser console using{' '}
            <code className="bg-muted px-1 py-0.5 rounded">window.audienceModeTests</code>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
